import React, { useState, useEffect, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import { Box, Typography, IconButton, Tooltip, ThemeProvider, CssBaseline, CircularProgress } from '@mui/material';
import GridViewRoundedIcon from '@mui/icons-material/GridViewRounded';
import PeopleAltRoundedIcon from '@mui/icons-material/PeopleAltRounded';
import VpnLockRoundedIcon from '@mui/icons-material/VpnLockRounded';
import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded';
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded';
import CommentRoundedIcon from '@mui/icons-material/CommentRounded';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import FilterAltRoundedIcon from '@mui/icons-material/FilterAltRounded';
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded';
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import AdminPanelSettingsRoundedIcon from '@mui/icons-material/AdminPanelSettingsRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import axios from 'axios';
import DashboardPage from './pages/DashboardPage';
import AccountsPage from './pages/AccountsPage';
import ProxiesPage from './pages/ProxiesPage';
import StatsPage from './pages/StatsPage';
import CampaignsPage from './pages/CampaignsPage';
import CommentsPage from './pages/CommentsPage';
import RulesPage from './pages/RulesPage';
import FunnelsPage from './pages/FunnelsPage';
import NotificationsPage from './pages/NotificationsPage';
import AutoUploadPage from './pages/AutoUploadPage';
import AdminPage from './pages/AdminPage';
import LoginPage from './pages/LoginPage';
import { getToken, clearToken } from './api';
import { createAppTheme, ColorModeContext } from './theme';
import { glowBackground, GradientText, LogoMark } from './ui';

const navItems = [
  { path: '/', label: 'Главная', icon: GridViewRoundedIcon },
  { path: '/accounts', label: 'Аккаунты', icon: PeopleAltRoundedIcon },
  { path: '/autoupload', label: 'Автозалив', icon: CloudUploadRoundedIcon },
  { path: '/proxies', label: 'Прокси', icon: VpnLockRoundedIcon },
  { path: '/stats', label: 'Статистика', icon: BarChartRoundedIcon },
  { path: '/campaigns', label: 'Кампании', icon: CampaignRoundedIcon },
  { path: '/comments', label: 'Комментарии', icon: CommentRoundedIcon },
  { path: '/rules', label: 'Правила', icon: BoltRoundedIcon },
  { path: '/funnels', label: 'Воронки', icon: FilterAltRoundedIcon },
  { path: '/notifications', label: 'Уведомления', icon: NotificationsActiveRoundedIcon },
];

// Мастер-аккаунт: полная навигация + доп. вкладка «Команда» (сводка по участникам).
const teamNavItem = { path: '/team', label: 'Команда', icon: AdminPanelSettingsRoundedIcon };

function Sidebar({ apiStatus, items = navItems }) {
  const location = useLocation();
  return (
    <Box sx={{
      width: 248, minWidth: 248, height: '100vh',
      display: 'flex', flexDirection: 'column',
      bgcolor: '#0d1117',
      borderRight: '1px solid', borderColor: 'divider',
      flexShrink: 0,
    }}>
      {/* Logo */}
      <Box sx={{ px: 3, pt: 3, pb: 2.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <LogoMark size={36} />
        <Box>
          <GradientText sx={{ fontSize: '16px', fontWeight: 600, letterSpacing: '-0.02em', lineHeight: 1.2, display: 'block' }}>
            Admetrix
          </GradientText>
          <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>Аналитика рекламы</Typography>
        </Box>
      </Box>

      <Box sx={{ mx: 3, mb: 1.5, height: '1px', bgcolor: 'divider' }} />

      {/* Nav */}
      <Box sx={{ flex: 1, px: 1.75, overflowY: 'auto' }}>
        <Typography sx={{
          px: 1.5, mb: 1, fontSize: '10.5px', fontWeight: 700,
          color: 'text.secondary', letterSpacing: '0.08em', textTransform: 'uppercase',
        }}>Навигация</Typography>

        {items.map(({ path, label, icon: Icon }) => {
          const active = location.pathname === path;
          return (
            <Link key={path} to={path} style={{ textDecoration: 'none', display: 'block' }}>
              {/* Пилюли навигации (pill vocabulary из референса GitHub) */}
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                px: 2, py: 1, mb: 0.5, borderRadius: '9999px', cursor: 'pointer',
                transition: 'background-color 0.2s ease',
                bgcolor: active ? 'rgba(255,255,255,0.10)' : 'transparent',
                border: '1px solid', borderColor: active ? 'rgba(255,255,255,0.12)' : 'transparent',
                '&:hover': { bgcolor: active ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.05)' },
              }}>
                <Icon sx={{ fontSize: 17, flexShrink: 0, color: active ? 'text.primary' : 'text.secondary', transition: 'color 0.2s' }} />
                <Typography sx={{
                  fontSize: '13.5px', fontWeight: active ? 600 : 500,
                  color: active ? 'text.primary' : 'text.secondary', letterSpacing: '-0.01em',
                }}>{label}</Typography>
                {active && <Box sx={{ ml: 'auto', width: 6, height: 6, borderRadius: '50%', bgcolor: 'success.main' }} />}
              </Box>
            </Link>
          );
        })}
      </Box>

      {/* API Status */}
      <Box sx={{ p: 2 }}>
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1.5, px: 1.75, py: 1.25, borderRadius: '11px',
          bgcolor: 'action.hover',
        }}>
          <Box sx={{
            width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
            bgcolor: apiStatus ? 'success.main' : 'error.main',
            boxShadow: theme => `0 0 0 3px ${apiStatus ? theme.palette.success.main : theme.palette.error.main}33`,
          }} />
          <Box>
            <Typography sx={{ fontSize: '10px', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em', lineHeight: 1 }}>API</Typography>
            <Typography sx={{ fontSize: '12.5px', fontWeight: 700, color: apiStatus ? 'success.main' : 'error.main', lineHeight: 1.5 }}>
              {apiStatus ? 'Подключено' : 'Отключено'}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function Topbar({ onLogout, items = navItems }) {
  const location = useLocation();
  const title = (items.find(n => n.path === location.pathname) || {}).label || 'Admetrix';
  return (
    <Box sx={{
      height: 60, flexShrink: 0, px: 3,
      display: 'flex', alignItems: 'center', gap: 1,
      bgcolor: 'transparent',
      borderBottom: '1px solid', borderColor: 'divider',
    }}>
      <Typography sx={{ fontSize: '17px', fontWeight: 600, color: 'text.primary', letterSpacing: '-0.02em' }}>
        {title}
      </Typography>
      <Box sx={{ flex: 1 }} />
      <Tooltip title="Выйти">
        <IconButton onClick={onLogout} sx={{ color: 'text.secondary' }}>
          <LogoutRoundedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

function AppLayout({ onLogout, isAdmin }) {
  const [apiStatus, setApiStatus] = useState(false);

  useEffect(() => {
    const check = () =>
      axios.get('/health').then(() => setApiStatus(true)).catch(() => setApiStatus(false));
    check();
    const iv = setInterval(check, 30000);
    return () => clearInterval(iv);
  }, []);

  const items = isAdmin ? [...navItems, teamNavItem] : navItems;

  return (
    <Box sx={(theme) => ({ display: 'flex', width: '100vw', height: '100vh', overflow: 'hidden', background: glowBackground(theme) })}>
      <Sidebar apiStatus={apiStatus} items={items} />
      <Box sx={{ flex: 1, minWidth: 0, height: '100vh', display: 'flex', flexDirection: 'column' }}>
        <Topbar onLogout={onLogout} items={items} />
        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', bgcolor: 'transparent' }}>
          <Routes>
            <Route path="/" element={<DashboardPage />} />
            <Route path="/accounts" element={<AccountsPage />} />
            <Route path="/autoupload" element={<AutoUploadPage />} />
            <Route path="/proxies" element={<ProxiesPage />} />
            <Route path="/stats" element={<StatsPage />} />
            <Route path="/campaigns" element={<CampaignsPage />} />
            <Route path="/comments" element={<CommentsPage />} />
            <Route path="/rules" element={<RulesPage />} />
            <Route path="/funnels" element={<FunnelsPage />} />
            <Route path="/notifications" element={<NotificationsPage />} />
            {isAdmin && <Route path="/team" element={<AdminPage />} />}
          </Routes>
        </Box>
      </Box>
    </Box>
  );
}

export default function App() {
  const [authed, setAuthed] = useState(!!getToken());
  const [me, setMe] = useState(null);          // { username, is_admin }
  const [meLoading, setMeLoading] = useState(!!getToken());

  useEffect(() => {
    if (!authed) { setMe(null); setMeLoading(false); return; }
    setMeLoading(true);
    axios.get('/auth/me')
      .then((r) => setMe(r.data))
      .catch(() => setMe({ is_admin: false }))
      .finally(() => setMeLoading(false));
  }, [authed]);

  const theme = useMemo(() => createAppTheme(), []);

  return (
    <ColorModeContext.Provider value={{ mode: 'dark', toggle: () => {} }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {authed ? (
          meLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100vw', height: '100vh', background: glowBackground() }}>
              <CircularProgress />
            </Box>
          ) : (
            <Router>
              <AppLayout isAdmin={!!(me && me.is_admin)} onLogout={() => { clearToken(); setMe(null); setAuthed(false); }} />
            </Router>
          )
        ) : (
          <LoginPage onLogin={() => setAuthed(true)} />
        )}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}
